import React, { useMemo, useState, useRef, useEffect } from "react";
import { useMeeting, useParticipant, usePubSub } from "../../config/sdkConfig";
import MicOffIcon from "../../icons/ParticipantTabPanel/MicOffIcon";
import MicOnIcon from "../../icons/ParticipantTabPanel/MicOnIcon";
import RaiseHand from "../../icons/ParticipantTabPanel/RaiseHand";
import VideoCamOffIcon from "../../icons/ParticipantTabPanel/VideoCamOffIcon";
import VideoCamOnIcon from "../../icons/ParticipantTabPanel/VideoCamOnIcon";
import { useMeetingAppContext } from "../../MeetingAppContextDef";
import { nameTructed } from "../../utils/helper";
import { useSiteContext } from "../../context/SiteContext";
import useIsMobile from "../../hooks/useIsMobile";
import useWindowSize from "../../hooks/useWindowSize";
import { HostPermissionConfirmModal, CoHostConfirmModal, KickParticipantConfirmModal } from "../modals/PermissionRequestModal";
import { useAgoraRTM } from "../../context/AgoraRTMContext";
import { stringToNumericUid } from "../../context/AgoraContext";
import * as meetingServices from "../../services/meetingServices";

const COLOR_PALETTE = [
  "#00b894", // Teal
  "#7700FF", // Purple
  "#FF6B6B", // Red
  "#4ECDC4", // Turquoise
  "#45B7D1", // Blue
  "#FFA07A", // Light Salmon
  "#98D8C8", // Mint
  "#F7DC6F", // Yellow
  "#BB8FCE", // Lavender
  "#85C1E2", // Sky Blue
  "#F8B739", // Orange
  "#52BE80", // Green
  "#EB984E", // Peach
  "#AF7AC5", // Plum
  "#76D7C4", // Aqua
  "#F1C40F", // Gold
  "#E74C3C", // Dark Red
  "#3498DB", // Royal Blue
  "#16A085", // Dark Teal
  "#D35400", // Dark Orange
];

function generateColorFromName(name) {
  if (!name) return COLOR_PALETTE[0];

  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
    hash = hash & hash;
  }

  const index = Math.abs(hash % COLOR_PALETTE.length);
  return COLOR_PALETTE[index];
}

function ParticipantListItem({
  participantId,
  raisedHand,
  canUnmute,
  onToggleUnmutePermission,
  isMobileLandscape,
  onRequestMicPermission,
  onRequestVideoPermission,
  onToggleCoHost,
  isParticipantCoHost,
  onMakePresenter,
  onDisableWebcam,
  onMuteMic,
  onKickParticipant,
  onStopScreenShare,
  isPresenting,
  isPresenter, // Whether participant has presenter permission (can share screen)
}) {
  const {
    micOn,
    webcamOn,
    displayName,
    isLocal,
    mode,
    disableMic,
    disableWebcam,
    pin,
    pinState,
    unpin,
    metaData,
  } = useParticipant(participantId);
  const isTargetHost = metaData?.meetingRole === 'moderator' || metaData?.meetingRole === 'host';
  const {
    meetingRole,
    selectedLayout,
    localPinnedParticipant,
    setLocalPinnedParticipant,
  } = useSiteContext();
  const { branding, participantBranding } = useMeetingAppContext();
  const canModerate = ["moderator", "co-host", "host"].includes(meetingRole);
  const isHost = meetingRole === "moderator" || meetingRole === "host";

  // Use branding lower third name if available (session-only name override)
  const ptBranding = !isLocal ? participantBranding?.[participantId] || participantBranding?.[String(participantId)] || participantBranding?.[stringToNumericUid(participantId)] : null;
  const effectiveName = isLocal
    ? (branding?.showLowerThird && branding?.lowerThirdName ? branding.lowerThirdName : displayName)
    : (ptBranding?.showLowerThird && ptBranding?.lowerThirdName ? ptBranding.lowerThirdName : displayName);
  const avatarColor = generateColorFromName(effectiveName);

  return (
    <div
      className={`bg-gray-700 rounded-lg mb-0 ${isMobileLandscape ? "mt-1 m-1 p-1" : "mt-2 m-2 p-2"
        }`}
    >
      <div className="flex flex-1 items-center justify-center relative">
        <div
          style={{
            color: "#ffffff",
            backgroundColor: avatarColor,
          }}
          className={`mt-0 rounded-full overflow-hidden flex relative items-center justify-center ${isMobileLandscape ? "h-7 w-7 text-sm" : "h-10 w-10 text-lg"
            }`}
        >
          {effectiveName?.charAt(0).toUpperCase()}
        </div>
        <div
          className={`mr-1 flex flex-1 ${isMobileLandscape ? "ml-1.5" : "ml-2"
            }`}
        >
          <p
            className={`text-white overflow-hidden whitespace-pre-wrap overflow-ellipsis ${isMobileLandscape ? "text-xs" : "text-base"
              }`}
          >
            {isLocal
              ? `${nameTructed(effectiveName, isMobileLandscape ? 10 : 12)} (You)`
              : nameTructed(effectiveName, isMobileLandscape ? 12 : 15)}
          </p>
          {/* Co-Host Badge */}
          {isParticipantCoHost && (
            <span
              className={`ml-1.5 px-1.5 py-0.5 rounded text-xs font-medium bg-blue-600 text-white ${isMobileLandscape ? "text-[10px] px-1 py-0" : ""
                }`}
            >
              Co-Host
            </span>
          )}
        </div>
        {raisedHand && (
          <div
            className={`flex items-center justify-center pointer-events-none ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
              }`}
            title="Raised hand"
          >
            <RaiseHand
              fillcolor={"#fff"}
              width={isMobileLandscape ? 14 : 18}
              height={isMobileLandscape ? 14 : 18}
            />
          </div>
        )}
        {["moderator", "co-host"].includes(meetingRole) && (
          <div
            className={`cursor-pointer ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
              }`}
            title={isPresenter ? "Remove Presenter" : "Make Presenter"}
            onClick={() => {
              if (["moderator", "co-host"].includes(meetingRole)) {
                // Publish to PubSub to sync presenter status with other participants
                onMakePresenter?.(participantId, displayName);
              }
            }}
          >
            {!isPresenter && (
              <svg
                width={isMobileLandscape ? "16" : "20"}
                height={isMobileLandscape ? "16" : "20"}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="#fff"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.295 19.562L16 22m1-6l3.758 2.098M19 12.5l3.026-.598M7.61 6.3a3 3 0 0 0-3.92 1.3l-1.38 2.79a3 3 0 0 0 1.3 3.91l6.89 3.597a1 1 0 0 0 1.342-.447l3.106-6.211a1 1 0 0 0-.447-1.341zM8 9V2"
                />
              </svg>
            )}
            {isPresenter && (
              <svg
                width={isMobileLandscape ? "16" : "20"}
                height={isMobileLandscape ? "16" : "20"}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
              >
                <path
                  fill="none"
                  stroke="#a259ec"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.295 19.562L16 22m1-6l3.758 2.098M19 12.5l3.026-.598M7.61 6.3a3 3 0 0 0-3.92 1.3l-1.38 2.79a3 3 0 0 0 1.3 3.91l6.89 3.597a1 1 0 0 0 1.342-.447l3.106-6.211a1 1 0 0 0-.447-1.341zM8 9V2"
                />
              </svg>
            )}
          </div>
        )}
        {selectedLayout === "gallery" && !isLocal && (
          <div
            className={`cursor-pointer ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
              }`}
            title={
              localPinnedParticipant.includes(participantId)
                ? "Unpin from your view"
                : "Pin to your view"
            }
            onClick={() => {
              console.log("pinState", pinState);
              if (localPinnedParticipant.includes(participantId)) {
                setLocalPinnedParticipant(
                  localPinnedParticipant.filter((p) => p !== participantId)
                );
              } else {
                setLocalPinnedParticipant([
                  ...localPinnedParticipant,
                  participantId,
                ]);
              }
            }}
          >
            {!localPinnedParticipant.includes(participantId) && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                width={isMobileLandscape ? "16" : "20"}
                height={isMobileLandscape ? "16" : "20"}
                fill="none"
                stroke="#fff"
                strokeWidth="32"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M368 384c0-70.7-57.3-128-128-128V192h32c8.8 0 16-7.2 16-16V48c0-8.8-7.2-16-16-16H112c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16h32v64C73.3 256 16 313.3 16 384c0 8.8 7.2 16 16 16h144v96c0 8.8 7.2 16 16 16s16-7.2 16-16v-96h144c8.8 0 16-7.2 16-16z" />
              </svg>
            )}
            {localPinnedParticipant.includes(participantId) && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 384 512"
                width={isMobileLandscape ? "16" : "20"}
                height={isMobileLandscape ? "16" : "20"}
                fill="none"
                stroke="#a259ec"
                strokeWidth="32"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M368 384c0-70.7-57.3-128-128-128V192h32c8.8 0 16-7.2 16-16V48c0-8.8-7.2-16-16-16H112c-8.8 0-16 7.2-16 16v128c0 8.8 7.2 16 16 16h32v64C73.3 256 16 313.3 16 384c0 8.8 7.2 16 16 16h144v96c0 8.8 7.2 16 16 16s16-7.2 16-16v-96h144c8.8 0 16-7.2 16-16z" />
              </svg>
            )}
          </div>
        )}
        {/* Mic control - visible to all, clickable for hosts */}
        {
          <div
            onClick={(e) => {
              e.stopPropagation();
              console.log(
                "role from participant panel",
                meetingRole,
                "micOn:",
                micOn,
                "participantId:",
                participantId
              );
              if (canModerate && !isLocal) {
                if (micOn) {
                  console.log("onMuteMic called from participant panel");
                  // Use PubSub to request remote participant to mute
                  onMuteMic?.(participantId);
                } else {
                  // If mic is off, request permission
                  onRequestMicPermission(participantId, displayName);
                }
              }
            }}
            className={`${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"} ${canModerate && !isLocal
              ? "cursor-pointer hover:opacity-80"
              : ""
              }`}
            title={
              micOn
                ? canModerate && !isLocal
                  ? "Mute mic"
                  : "Mic on"
                : canModerate && !isLocal
                  ? "Request to unmute"
                  : "Mic off"
            }
          >
            {micOn ? (
              <MicOnIcon
                width={isMobileLandscape ? 14 : 18}
                height={isMobileLandscape ? 14 : 18}
              />
            ) : (
              <MicOffIcon
                width={isMobileLandscape ? 14 : 18}
                height={isMobileLandscape ? 14 : 18}
              />
            )}
          </div>
        }
        {/* Camera control - visible to all, clickable for hosts */}
        {
          <div
            onClick={() => {
              if (canModerate && !isLocal) {
                if (webcamOn) {
                  // Use PubSub to request remote participant to disable webcam
                  onDisableWebcam?.(participantId);
                } else {
                  // If camera is off, request permission to enable
                  onRequestVideoPermission(participantId, displayName);
                }
              }
            }}
            className={`${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"} ${canModerate && !isLocal
              ? "cursor-pointer hover:opacity-80"
              : ""
              }`}
            title={
              webcamOn
                ? canModerate && !isLocal
                  ? "Disable camera"
                  : "Camera on"
                : canModerate && !isLocal
                  ? "Request to enable camera"
                  : "Camera off"
            }
          >
            {webcamOn ? (
              <VideoCamOnIcon
                width={isMobileLandscape ? 14 : 18}
                height={isMobileLandscape ? 14 : 18}
              />
            ) : (
              <VideoCamOffIcon
                width={isMobileLandscape ? 14 : 18}
                height={isMobileLandscape ? 14 : 18}
              />
            )}
          </div>
        }

        {/* Host-only controls: Co-Host toggle and Remove participant */}
        {canModerate && !isLocal && (
          <>
            {/* Make/Remove Co-Host - Only visible to Host (moderator), hidden for host targets */}
            {isHost && !isTargetHost && (
              <div
                className={`cursor-pointer hover:opacity-80 transition-opacity ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
                  }`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleCoHost(participantId, displayName, isParticipantCoHost);
                }}
                title={isParticipantCoHost ? "Remove Co-Host" : "Make Co-Host"}
              >
                {isParticipantCoHost ? (
                  // Shield with X icon - Remove Co-Host
                  <svg
                    width={isMobileLandscape ? "14" : "18"}
                    height={isMobileLandscape ? "14" : "18"}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#60a5fa"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                  </svg>
                ) : (
                  // Shield with plus icon - Make Co-Host
                  <svg
                    width={isMobileLandscape ? "14" : "18"}
                    height={isMobileLandscape ? "14" : "18"}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="#9ca3af"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
                    <line x1="12" y1="9" x2="12" y2="15" />
                    <line x1="9" y1="12" x2="15" y2="12" />
                  </svg>
                )}
              </div>
            )}
            {/* Stop Screen Share - Only visible when participant is presenting and target is not host/co-host */}
            {isPresenting && !isTargetHost && (isHost || !isParticipantCoHost) && (
              <div
                className={`cursor-pointer hover:bg-orange-500/20 rounded transition-colors ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
                  }`}
                onClick={() => onStopScreenShare?.(participantId, displayName)}
                title="Stop screen share"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width={isMobileLandscape ? "14" : "16"}
                  height={isMobileLandscape ? "14" : "16"}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="text-orange-400 hover:text-orange-300"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2"></rect>
                  <line x1="8" y1="21" x2="16" y2="21"></line>
                  <line x1="12" y1="17" x2="12" y2="21"></line>
                  <line x1="2" y1="3" x2="22" y2="17" stroke="#f97316"></line>
                </svg>
              </div>
            )}
            {/* Remove Participant */}
            <div
              className={`cursor-pointer hover:bg-red-500/20 rounded transition-colors ${isMobileLandscape ? "m-0.5 p-0.5" : "m-1 p-1"
                }`}
              onClick={() => onKickParticipant?.(participantId, displayName)}
              title="Remove from meeting"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={isMobileLandscape ? "14" : "16"}
                height={isMobileLandscape ? "14" : "16"}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-red-400 hover:text-red-300"
              >
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <line x1="17" y1="8" x2="23" y2="8"></line>
              </svg>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ParticipantPanel({ panelHeight }) {
  const {
    raisedHandsParticipants,
    participantApproveFunc,
    setParticipantApproveFunc,
    activeSpeakerId,
    presenters,
    setPresenters,
  } = useMeetingAppContext();
  const mMeeting = useMeeting();
  const participants = mMeeting.participants;
  const { localParticipant } = mMeeting;
  const { meetingRole, coHosts, isCoHost, wasDemotedCoHost, addCoHost, removeCoHost, meetingSettings, user } = useSiteContext();
  // Get presence snapshot from RTM for reliable mute state verification
  const rtm = useAgoraRTM();
  const { publish: publishMuteMic } = usePubSub("MUTE_MIC");
  const { publish: publishRoleChange } = usePubSub("ROLE_CHANGE");
  const { publish: publishParticipantUnmutePermission } = usePubSub(
    "PARTICIPANT_UNMUTE_PERMISSION"
  );
  const { publish: publishMicPermissionRequest } = usePubSub(
    "MIC_PERMISSION_REQUEST"
  );
  const { publish: publishVideoPermissionRequest } = usePubSub(
    "VIDEO_PERMISSION_REQUEST"
  );
  const { publish: publishUnmutePermission } = usePubSub(
    "PARTICIPANT_UNMUTE_PERMISSION"
  );
  const { publish: publishSpotlight } = usePubSub("SPOTLIGHT_PARTICIPANT");
  const { publish: publishMakePresenter } = usePubSub("MAKE_PRESENTER");
  const { publish: publishDisableWebcam } = usePubSub("DISABLE_WEBCAM");
  const { publish: publishLowerHand } = usePubSub("LOWER_HAND");
  const { publish: publishKickParticipant } = usePubSub("KICK_PARTICIPANT");
  const { publish: publishStopScreenShare } = usePubSub("STOP_SCREEN_SHARE");
  const presenterId = mMeeting?.presenterId;
  const [searchQuery, setSearchQuery] = useState("");
  const [showMutedOnly, setShowMutedOnly] = useState(false);
  const [participantUnmutePermissions, setParticipantUnmutePermissions] =
    useState(new Map()); // Track per-participant unmute permissions
  const speakerHistoryRef = useRef([]); // Track last 3 active speakers
  const previousActiveSpeakerRef = useRef(null); // Track previous active speaker

  // Permission request modal states
  const [showHostMicConfirm, setShowHostMicConfirm] = useState(false);
  const [showHostVideoConfirm, setShowHostVideoConfirm] = useState(false);
  const [pendingPermissionRequest, setPendingPermissionRequest] =
    useState(null);

  // Co-Host modal states
  const [showCoHostConfirm, setShowCoHostConfirm] = useState(false);
  const [pendingCoHostRequest, setPendingCoHostRequest] = useState(null);
  const [allMuted, setAllMuted] = useState(false);

  // Kick participant modal states
  const [showKickConfirm, setShowKickConfirm] = useState(false);
  const [pendingKickRequest, setPendingKickRequest] = useState(null);

  // Force re-render for waiting time updates
  const [, setTimeUpdate] = useState(0);

  // Update waiting times every second
  useEffect(() => {
    if (participantApproveFunc?.length > 0) {
      const interval = setInterval(() => {
        setTimeUpdate((n) => n + 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [participantApproveFunc?.length]);

  // Format elapsed time since joinedAt
  const getElapsedTime = (joinedAt) => {
    if (!joinedAt) return "just now";
    const seconds = Math.floor((Date.now() - joinedAt) / 1000);
    if (seconds < 10) return "just now";
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    if (mins < 60) return `${mins}m ${secs}s`;
    const hours = Math.floor(mins / 60);
    const remainingMins = mins % 60;
    return `${hours}h ${remainingMins}m`;
  };

  const isMobile = useIsMobile();
  const windowSize = useWindowSize();
  const isLandscapeOrientation =
    (windowSize?.width || 0) > 0 &&
    (windowSize?.height || 0) > 0 &&
    windowSize.width > windowSize.height;
  const isMobileLandscape = isMobile && isLandscapeOrientation;

  // Calculate if all mutable remote participants are muted
  // Excludes hosts, co-hosts, and presenters (they won't be muted by "Mute All")
  useEffect(() => {
    const participantIds = [...participants.keys()];

    // Filter to only include participants that CAN be muted (not local, not host/co-host, not presenter)
    const mutableParticipants = participantIds.filter((id) => {
      if (id === localParticipant?.id) return false;

      const participant = participants.get(id);
      const participantRole = participant?.metaData?.meetingRole;
      const isHostOrCoHost = ["moderator", "host"].includes(participantRole) || isCoHost(id) || (participantRole === 'co-host' && !wasDemotedCoHost(id));
      const isPresenter = presenterId === id;

      return !isHostOrCoHost && !isPresenter;
    });

    if (mutableParticipants.length === 0) {
      setAllMuted(false);
      return;
    }

    const allMutableMuted = mutableParticipants.every((participantId) => {
      const participant = participants.get(participantId);
      if (!participant?.micOn) return true;
      // Also check presence state for a more reliable indicator
      const presenceState = rtm?.presenceSnapshot?.get?.(String(participantId))?.states;
      if (presenceState?.micMuted === "true") return true;
      return false;
    });

    setAllMuted(allMutableMuted);
  }, [participants, localParticipant, presenterId, isCoHost]);

  // Subscribe to unmute permission changes from other moderators
  usePubSub("PARTICIPANT_UNMUTE_PERMISSION", {
    onMessageReceived: (data) => {
      if (data?.participantId && typeof data?.canUnmute === "boolean") {
        setParticipantUnmutePermissions((prev) => {
          const newMap = new Map(prev);
          if (data.canUnmute) {
            // Remove from map means allowed (default)
            newMap.delete(data.participantId);
          } else {
            // Set to false means disabled
            newMap.set(data.participantId, false);
          }
          return newMap;
        });
      }
    },
  });

  // Handle requesting mic permission from host/co-host
  const handleRequestMicPermission = (participantId, participantName) => {
    setPendingPermissionRequest({
      participantId,
      participantName,
      type: "mic",
    });
    setShowHostMicConfirm(true);
  };

  // Handle requesting video permission from host/co-host
  const handleRequestVideoPermission = (participantId, participantName) => {
    setPendingPermissionRequest({
      participantId,
      participantName,
      type: "video",
    });
    setShowHostVideoConfirm(true);
  };

  // Confirm mic permission request
  const handleConfirmMicPermission = () => {
    if (pendingPermissionRequest) {
      // Send permission to unmute
      publishUnmutePermission(
        {
          participantId: pendingPermissionRequest.participantId,
          canUnmute: true,
        },
        { persist: false }
      );

      // Send the modal notification
      publishMicPermissionRequest(
        {
          targetParticipantId: pendingPermissionRequest.participantId,
          granted: true,
        },
        { persist: false }
      );
      console.log(
        "Mic permission request sent to:",
        pendingPermissionRequest.participantId
      );
    }
    setShowHostMicConfirm(false);
    setPendingPermissionRequest(null);
  };

  // Confirm video permission request
  const handleConfirmVideoPermission = () => {
    if (pendingPermissionRequest) {
      publishVideoPermissionRequest(
        {
          targetParticipantId: pendingPermissionRequest.participantId,
          granted: true,
        },
        { persist: false }
      );
      console.log(
        "Video permission request sent to:",
        pendingPermissionRequest.participantId
      );
    }
    setShowHostVideoConfirm(false);
    setPendingPermissionRequest(null);
  };

  // Cancel permission request
  const handleCancelPermission = () => {
    setShowHostMicConfirm(false);
    setShowHostVideoConfirm(false);
    setPendingPermissionRequest(null);
  };

  // Handle toggling co-host status
  const handleToggleCoHost = (participantId, participantName, isCurrentlyCoHost) => {
    setPendingCoHostRequest({
      participantId,
      participantName,
      isCurrentlyCoHost,
    });
    setShowCoHostConfirm(true);
  };

  // Confirm co-host promotion/demotion
  const handleConfirmCoHost = () => {
    if (pendingCoHostRequest) {
      const { participantId, isCurrentlyCoHost } = pendingCoHostRequest;
      const action = isCurrentlyCoHost ? 'demote' : 'promote';
      const newRole = isCurrentlyCoHost ? 'participant' : 'co-host';

      // Update local state immediately
      if (action === 'promote') {
        addCoHost(participantId);
      } else {
        removeCoHost(participantId);
      }

      // Publish role change to all participants
      publishRoleChange(
        {
          targetParticipantId: participantId,
          newRole,
          action,
        },
        { persist: true }
      );

      // Persist co-host to DB for logged-in users (check target's presence for userId)
      // Try direct lookup first, then search by numeric UID (Agora UID mismatch: remote participants
      // have numeric UIDs in RTC but string IDs in RTM presence)
      let targetPresence = rtm?.presenceSnapshot?.get?.(String(participantId))?.states;
      if (!targetPresence && rtm?.presenceSnapshot) {
        const numericId = typeof participantId === 'number' ? participantId : Number(participantId);
        if (!isNaN(numericId)) {
          for (const [rtmUserId, entry] of rtm.presenceSnapshot) {
            if (stringToNumericUid(rtmUserId) === numericId) {
              targetPresence = entry?.states;
              break;
            }
          }
        }
      }
      const targetUserId = targetPresence?.userId;
      if (targetUserId && meetingSettings?.roomId) {
        meetingServices.updateCoHost({
          meetingId: meetingSettings.roomId,
          targetUserId,
          action,
        }).catch((err) => {
          console.error('Failed to persist co-host to DB:', err);
        });
      }

      console.log(
        `Co-host ${action} for:`,
        pendingCoHostRequest.participantName,
        participantId
      );
    }
    setShowCoHostConfirm(false);
    setPendingCoHostRequest(null);
  };

  // Cancel co-host toggle
  const handleCancelCoHost = () => {
    setShowCoHostConfirm(false);
    setPendingCoHostRequest(null);
  };

  // Handle make presenter - grants screen share permission to attendee
  // This is separate from spotlight which only highlights video
  const handleMakePresenter = (participantId, displayName) => {
    // Check if participant is already a presenter using the presenters state
    // Only check original ID and string version (not numeric hash to avoid collisions)
    const isCurrentlyPresenter = presenters?.has(participantId) ||
      presenters?.has(String(participantId));
    const action = isCurrentlyPresenter ? 'remove' : 'add';

    // Update local state immediately for instant UI feedback
    // Only store original ID and string version (not numeric hash)
    setPresenters(prev => {
      const newSet = new Set(prev);
      if (action === 'add') {
        newSet.add(participantId);
        newSet.add(String(participantId));
      } else {
        newSet.delete(participantId);
        newSet.delete(String(participantId));
      }
      return newSet;
    });

    // Publish to sync with other participants
    publishMakePresenter(
      JSON.stringify({
        participantId,
        displayName: displayName || 'Participant',
        action
      }),
      { persist: true }
    );

    console.log(`Make presenter: ${action} for ${displayName} (${participantId})`);
  };

  // Handle disabling remote participant's webcam - broadcasts to target participant
  const handleDisableWebcam = (participantId) => {
    publishDisableWebcam(
      { participantId },
      { persist: false }
    );
    console.log(`Disable webcam request sent to: ${participantId}`);
  };

  // Handle muting remote participant - broadcasts to target participant
  const handleMuteMic = (participantId) => {
    publishMuteMic(
      { participantId, senderName: localParticipant?.displayName || 'Host' },
      { persist: false }
    );
    console.log(`Mute mic request sent to: ${participantId}`);
  };

  // Handle kick/remove participant from meeting - shows confirmation modal first
  const handleKickParticipant = (participantId, displayName) => {
    setPendingKickRequest({ participantId, participantName: displayName || 'Participant' });
    setShowKickConfirm(true);
  };

  // Confirm kick participant
  const handleConfirmKick = () => {
    if (pendingKickRequest) {
      console.log(`Kicking participant: ${pendingKickRequest.participantName} (${pendingKickRequest.participantId})`);
      publishKickParticipant(
        JSON.stringify({
          targetParticipantId: pendingKickRequest.participantId,
          displayName: pendingKickRequest.participantName,
          kickedBy: localParticipant?.displayName || 'Host',
        }),
        { persist: false }
      );
    }
    setShowKickConfirm(false);
    setPendingKickRequest(null);
  };

  // Cancel kick participant
  const handleCancelKick = () => {
    setShowKickConfirm(false);
    setPendingKickRequest(null);
  };

  // Handle stopping a participant's screen share
  const handleStopScreenShare = (participantId, displayName) => {
    console.log(`Stopping screen share for: ${displayName} (${participantId})`);
    publishStopScreenShare(
      { participantId },
      { persist: false }
    );
  };

  // Handle lowering a participant's raised hand
  const handleLowerHand = (participantId) => {
    publishLowerHand(
      { participantId },
      { persist: false }
    );
    console.log(`Lower hand request sent to: ${participantId}`);
  };

  // Initialize permissions: by default, all participants can unmute unless explicitly disabled
  // Only moderators can toggle this
  const handleToggleUnmutePermission = (participantId) => {
    const currentPermission =
      participantUnmutePermissions.get(participantId) !== false;
    const newPermission = !currentPermission;

    setParticipantUnmutePermissions((prev) => {
      const newMap = new Map(prev);
      if (newPermission) {
        newMap.delete(participantId); // Remove from map means allowed (default)
      } else {
        newMap.set(participantId, false); // Explicitly set to false means disabled
      }
      return newMap;
    });

    // Publish the permission change to other participants
    publishParticipantUnmutePermission({
      participantId,
      canUnmute: newPermission,
    });
  };

  const sortedRaisedHandsParticipants = useMemo(() => {
    const participantIds = [...participants.keys()];

    const notRaised = participantIds.filter(
      (pID) =>
        raisedHandsParticipants.findIndex(
          ({ participantId: rPID }) => rPID === pID
        ) === -1
    );

    // Sort by raisedHandOn ascending (chronological order - first raised appears first)
    const raisedSorted = [...raisedHandsParticipants].sort((a, b) => {
      return a.raisedHandOn - b.raisedHandOn;
    });

    const combined = [
      ...raisedSorted.map(({ participantId: p }) => ({
        raisedHand: true,
        participantId: p,
      })),
      ...notRaised.map((p) => ({ raisedHand: false, participantId: p })),
    ];

    return combined;
  }, [raisedHandsParticipants, participants]);

  const part = useMemo(() => {
    let filtered = sortedRaisedHandsParticipants;

    // Filter by muted status if enabled
    if (showMutedOnly) {
      filtered = filtered.filter(({ participantId }) => {
        if (participantId === localParticipant?.id) {
          return !localParticipant?.micOn;
        }
        const participant = participants.get(participantId);
        return !participant?.micOn;
      });
    }

    // Filter by search query if provided
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      filtered = filtered.filter(({ participantId }) => {
        const participant = participants.get(participantId);
        const displayName = participant?.displayName || "";
        return displayName.toLowerCase().includes(query);
      });
    }

    // Update speaker history synchronously before sorting
    if (activeSpeakerId) {
      const previousActive = previousActiveSpeakerRef.current;

      // If active speaker changed (and we had a previous one)
      if (previousActive && previousActive !== activeSpeakerId) {
        // Remove previous active speaker from history if it exists
        let currentHistory = speakerHistoryRef.current.filter(
          (id) => id !== previousActive && id !== activeSpeakerId
        );

        // Add previous speaker to front of history
        speakerHistoryRef.current = [previousActive, ...currentHistory].slice(
          0,
          3
        );
      }

      // Remove current active speaker from history (if it exists)
      speakerHistoryRef.current = speakerHistoryRef.current.filter(
        (id) => id !== activeSpeakerId
      );

      // Update previous active speaker ref
      previousActiveSpeakerRef.current = activeSpeakerId;
    } else if (!activeSpeakerId) {
      // Clear previous if no active speaker
      previousActiveSpeakerRef.current = null;
    }

    // Sort: 1) Active speaker, 2) Last 3 speakers, 3) Participants with video on, 4) Others
    const sortedList = [];
    const remaining = [...filtered];

    const getParticipantWebcamStatus = (participantId) => {
      if (participantId === localParticipant?.id) {
        return localParticipant?.webcamOn || false;
      }
      const participant = participants.get(participantId);
      return participant?.webcamOn || false;
    };

    // 1. Current active speaker (first)
    if (activeSpeakerId) {
      const activeIndex = remaining.findIndex(
        ({ participantId }) => participantId === activeSpeakerId
      );
      if (activeIndex !== -1) {
        sortedList.push(remaining[activeIndex]);
        remaining.splice(activeIndex, 1);
      }
    }

    // 2. Last 3 speakers (in order: last speaker 1, 2, 3)
    const history = speakerHistoryRef.current;
    for (let i = 0; i < Math.min(3, history.length); i++) {
      const lastSpeakerId = history[i];
      const historyIndex = remaining.findIndex(
        ({ participantId }) => participantId === lastSpeakerId
      );
      if (historyIndex !== -1) {
        sortedList.push(remaining[historyIndex]);
        remaining.splice(historyIndex, 1);
      }
    }

    // 3. Participants with video on
    const videoOnItems = remaining.filter(({ participantId }) =>
      getParticipantWebcamStatus(participantId)
    );
    sortedList.push(...videoOnItems);
    videoOnItems.forEach((item) => {
      const index = remaining.indexOf(item);
      if (index > -1) {
        remaining.splice(index, 1);
      }
    });

    // 4. Others (participants without video)
    sortedList.push(...remaining);

    return sortedList;
  }, [
    sortedRaisedHandsParticipants,
    participants,
    searchQuery,
    activeSpeakerId,
    localParticipant,
    showMutedOnly,
  ]);

  const handleMuteAll = () => {
    // Get all participants
    const participantIds = [...participants.keys()];

    // Mute all participants except the local user, hosts/co-hosts, and presenter
    participantIds.forEach((participantId) => {
      const participant = participants.get(participantId);
      const participantRole = participant?.metaData?.meetingRole;
      const isHostOrCoHost = ["moderator", "host"].includes(participantRole) || isCoHost(participantId) || (participantRole === 'co-host' && !wasDemotedCoHost(participantId));
      const isPresenter = presenterId === participantId;

      // Only mute if participant has mic on, is not local, is not a host/co-host, and is not presenting
      if (
        participant?.micOn &&
        !isHostOrCoHost &&
        !isPresenter &&
        !participant?.isLocal
      ) {
        // Use PubSub to request remote participant to mute (required for Agora)
        publishMuteMic(
          {
            participantId: participantId,
          },
          {
            persist: false,
          }
        );
      }
    });
  };

  // Request all muted participants to unmute
  const handleRequestUnmuteAll = () => {
    const participantIds = [...participants.keys()];

    // Send unmute permission request to all muted remote participants
    participantIds.forEach((participantId) => {
      const participant = participants.get(participantId);
      // Only request unmute if participant has mic off and is not local
      if (!participant?.micOn && !participant?.isLocal) {
        // Send permission to unmute
        publishUnmutePermission(
          {
            participantId: participantId,
            canUnmute: true,
          },
          { persist: false }
        );

        // Send the modal notification to the participant
        publishMicPermissionRequest(
          {
            targetParticipantId: participantId,
            granted: true,
          },
          { persist: false }
        );
      }
    });
  };

  return (
    <>
      {/* Host Confirmation Modals */}
      <HostPermissionConfirmModal
        open={showHostMicConfirm}
        onConfirm={handleConfirmMicPermission}
        onCancel={handleCancelPermission}
        participantName={pendingPermissionRequest?.participantName || ""}
        permissionType="mic"
      />
      <HostPermissionConfirmModal
        open={showHostVideoConfirm}
        onConfirm={handleConfirmVideoPermission}
        onCancel={handleCancelPermission}
        participantName={pendingPermissionRequest?.participantName || ""}
        permissionType="video"
      />

      {/* Co-Host Confirmation Modal */}
      <CoHostConfirmModal
        open={showCoHostConfirm}
        onConfirm={handleConfirmCoHost}
        onCancel={handleCancelCoHost}
        participantName={pendingCoHostRequest?.participantName || ""}
        isRemoving={pendingCoHostRequest?.isCurrentlyCoHost || false}
      />

      {/* Kick Participant Confirmation Modal */}
      <KickParticipantConfirmModal
        open={showKickConfirm}
        onConfirm={handleConfirmKick}
        onCancel={handleCancelKick}
        participantName={pendingKickRequest?.participantName || ""}
      />

      <div
        className={`flex w-full flex-col bg-gray-750 overflow-y-auto `}
        style={{ height: panelHeight }}
      >
        {(["moderator", "co-host"].includes(meetingRole) || isCoHost(localParticipant?.id)) && (
          <div
            className={`flex items-center justify-between border-b border-gray-700 ${isMobileLandscape ? "p-1.5" : "p-3"
              }`}
          >
            {allMuted ? (
              <button
                onClick={handleRequestUnmuteAll}
                className={`${isMobileLandscape ? "px-2 py-1 text-xs" : "px-3 py-1 text-sm"
                  } bg-green-600 text-white rounded hover:bg-green-700 transition-colors flex items-center gap-1`}
              >
                <MicOnIcon width={isMobileLandscape ? 12 : 14} height={isMobileLandscape ? 12 : 14} />
                Unmute All
              </button>
            ) : (
              <button
                onClick={handleMuteAll}
                className={`${isMobileLandscape ? "px-2 py-1 text-xs" : "px-3 py-1 text-sm"
                  } bg-red-600 text-white rounded hover:bg-red-700 transition-colors flex items-center gap-1`}
              >
                <MicOffIcon width={isMobileLandscape ? 12 : 14} height={isMobileLandscape ? 12 : 14} />
                Mute All
              </button>
            )}
          </div>
        )}
        {/* Waiting Room Section */}
        {participantApproveFunc.length > 0 && (
          <div className="border-b border-gray-700">
            {/* Waiting Room Header */}
            <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-[#6a1fbf]/20 to-[#00c853]/20 border-b border-white/10">
              <div className="flex items-center gap-2">
                <span className="text-white font-semibold text-sm">Waiting Room</span>
                <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full animate-pulse">
                  {participantApproveFunc.length}
                </span>
              </div>
              {participantApproveFunc.length > 1 && (
                <button
                  onClick={() => {
                    participantApproveFunc.forEach((item) => item.allow());
                    setParticipantApproveFunc([]);
                  }}
                  className="px-3 py-1 text-xs font-medium bg-gradient-to-r from-[#6a1fbf] to-[#00c853] text-white rounded-lg hover:opacity-90 transition-opacity"
                >
                  Admit All
                </button>
              )}
            </div>

            {/* Waiting Participants List */}
            <div
              className="flex flex-col"
              style={{
                maxHeight: participantApproveFunc.length > 4 ? "40vh" : "auto",
                overflowY: "auto",
              }}
            >
              {participantApproveFunc.map((item) => {
                const bgColor = generateColorFromName(item.name);
                const initials = item.name
                  ? item.name.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2)
                  : "?";

                return (
                  <div
                    key={item.participantId}
                    className="flex items-center justify-between p-3 hover:bg-white/5 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
                        style={{ backgroundColor: bgColor }}
                      >
                        {initials}
                      </div>
                      <div className="flex flex-col">
                        <span className="text-white text-sm font-medium">{item.name}</span>
                        <span className="text-gray-400 text-xs flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-yellow-500 rounded-full animate-pulse"></span>
                          Waiting {getElapsedTime(item.joinedAt)}
                        </span>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          item.allow();
                          setParticipantApproveFunc(
                            participantApproveFunc.filter(
                              (pitem) => pitem.participantId !== item.participantId
                            )
                          );
                        }}
                        className="px-3 py-1.5 text-xs font-medium bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 transition-colors"
                      >
                        Admit
                      </button>
                      <button
                        onClick={() => {
                          item.deny();
                          setTimeout(() => {
                            setParticipantApproveFunc(
                              participantApproveFunc.filter(
                                (pitem) => pitem.participantId !== item.participantId
                              )
                            );
                          }, 500);
                        }}
                        className="px-3 py-1.5 text-xs font-medium bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
                      >
                        Deny
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Search Input and Muted Filter */}
        <div
          className={`border-b border-gray-700 ${isMobileLandscape ? "p-1.5" : "p-3"
            } ${isMobileLandscape ? "space-y-1" : "space-y-2"}`}
        >
          {isMobileLandscape ? (
            // Landscape: Horizontal compact layout
            <div className="flex gap-1.5 items-center">
              <div className="relative flex-1">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search..."
                  className="w-full px-2 py-1 pl-7 text-xs bg-gray-800 border border-gray-600 rounded text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-indigo-500"
                />
                <svg
                  className="absolute left-2 top-1/2 transform -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-1.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    title="Clear search"
                  >
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              {/* Muted Filter Toggle - Compact */}
              <button
                onClick={() => setShowMutedOnly(!showMutedOnly)}
                className={`px-2 py-1 text-xs rounded transition-colors flex items-center justify-center gap-1 flex-shrink-0 ${showMutedOnly
                  ? "bg-[#7700FF] text-white hover:bg-[#7700FF]"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
                title={showMutedOnly ? "Show All" : "Show Muted Only"}
              >
                <MicOffIcon width={14} height={14} />
                <span className="hidden sm:inline">
                  {showMutedOnly ? "All" : "Muted"}
                </span>
              </button>
            </div>
          ) : (
            // Portrait/Desktop: Vertical layout
            <>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search participants..."
                  className="w-full px-4 py-2 pl-10 bg-gray-800 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                <svg
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                    title="Clear search"
                  >
                    <svg
                      className="w-5 h-5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                )}
              </div>
              {/* Muted Filter Toggle */}
              <button
                onClick={() => setShowMutedOnly(!showMutedOnly)}
                className={`w-full px-3 py-2 text-sm rounded-lg transition-colors flex items-center justify-center gap-2 ${showMutedOnly
                  ? "bg-[#7700FF] text-white hover:bg-[#7700FF]"
                  : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  }`}
              >
                <MicOffIcon />
                <span>{showMutedOnly ? "Show All" : "Show Muted Only"}</span>
              </button>
            </>
          )}
        </div>

        <div
          className="flex flex-col flex-1 overflow-y-auto"
          style={{ height: panelHeight - 100 }}
        >
          {part.map(({ raisedHand, participantId: peerId }) => {
            if (!peerId) return null;
            const canUnmute =
              participantUnmutePermissions.get(peerId) !== false; // Default to true (allowed)
            const peerMeta = participants.get(peerId)?.metaData?.meetingRole;
            const participantIsCoHost = isCoHost(peerId) || (peerMeta === 'co-host' && !wasDemotedCoHost(peerId));
            // Check if participant is a presenter - only match on exact ID or string version
            // Don't match on numeric hash to avoid hash collisions with different participants
            const participantIsPresenter = presenters?.has(peerId) ||
              presenters?.has(String(peerId));
            return (
              <ParticipantListItem
                key={peerId}
                participantId={peerId}
                raisedHand={raisedHand}
                canUnmute={canUnmute}
                onToggleUnmutePermission={handleToggleUnmutePermission}
                isMobileLandscape={isMobileLandscape}
                onRequestMicPermission={handleRequestMicPermission}
                onRequestVideoPermission={handleRequestVideoPermission}
                onToggleCoHost={handleToggleCoHost}
                isParticipantCoHost={participantIsCoHost}
                onMakePresenter={handleMakePresenter}
                onDisableWebcam={handleDisableWebcam}
                onMuteMic={handleMuteMic}
                onKickParticipant={handleKickParticipant}
                onStopScreenShare={handleStopScreenShare}
                isPresenting={presenterId === peerId}
                isPresenter={participantIsPresenter}
              />
            );
          })}
        </div>
      </div>
    </>
  );
}
